/**
 * Build a CSS selector that uniquely identifies an element in the document.
 *
 * Prefers stable hooks (`data-testid`, `id`) and falls back to an
 * `nth-of-type` path from the nearest stable ancestor.
 */

function escapeIdentifier(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }
    return value.replace(/([^\w-])/g, '\\$1');
}

function isUnique(root: Document, selector: string): boolean {
    try {
        return root.querySelectorAll(selector).length === 1;
    } catch {
        return false;
    }
}

/** Selector for an element on its own, ignoring ancestors. */
function stableSelfSelector(element: Element): string | null {
    const testId = element.getAttribute('data-testid');
    if (testId) return `[data-testid="${testId.replace(/"/g, '\\"')}"]`;
    if (element.id) return `#${escapeIdentifier(element.id)}`;
    return null;
}

function nthOfTypeSelector(element: Element): string {
    const tag = element.tagName.toLowerCase();
    const parent = element.parentElement;
    if (!parent) return tag;

    const siblings = Array.from(parent.children).filter(
        (child) => child.tagName === element.tagName
    );
    if (siblings.length === 1) return tag;
    return `${tag}:nth-of-type(${siblings.indexOf(element) + 1})`;
}

export function buildCssSelector(element: Element): string {
    const root = element.ownerDocument;

    const self = stableSelfSelector(element);
    if (self && isUnique(root, self)) return self;

    const path: string[] = [];
    let current: Element | null = element;

    while (current && current !== root.documentElement) {
        const anchor = stableSelfSelector(current);
        if (anchor) {
            path.unshift(anchor);
            const candidate = path.join(' > ');
            if (isUnique(root, candidate)) return candidate;
            current = current.parentElement;
            continue;
        }

        path.unshift(nthOfTypeSelector(current));
        const candidate = path.join(' > ');
        if (isUnique(root, candidate)) return candidate;

        current = current.parentElement;
    }

    // ponytail: a non-unique path still points a human at the right place.
    return path.join(' > ') || element.tagName.toLowerCase();
}

/** Short human-readable description of an element for the Linear issue. */
export function describeElement(element: Element): string {
    const parts = [`<${element.tagName.toLowerCase()}>`];

    const text = element.textContent?.trim().replace(/\s+/g, ' ').slice(0, 120);
    if (text) parts.push(`"${text}"`);

    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) parts.push(`aria-label="${ariaLabel}"`);

    const rect = element.getBoundingClientRect();
    parts.push(
        `at ${Math.round(rect.left)},${Math.round(rect.top)} (${Math.round(rect.width)}×${Math.round(rect.height)})`
    );

    return parts.join(' ');
}
